import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useLang } from "../i18n";

const draw = {
  hidden: { pathLength: 0, opacity: 0 },
  visible: (i) => ({
    pathLength: 1,
    opacity: 1,
    transition: {
      pathLength: { delay: 0.15 + i * 0.22, duration: 1.05, ease: [0.4, 0, 0.2, 1] },
      opacity: { delay: 0.15 + i * 0.22, duration: 0.25 },
    },
  }),
};

const STROKES = [
  "M30 118 L130 42 L230 118",
  "M48 118 V212 H212 V118",
  "M112 212 V168 a18 18 0 0 1 36 0 V212",
  "M70 140 h22 M168 140 h22",
  "M16 212 H244",
  "M26 212 C22 176 20 158 26 128 C32 158 30 176 26 212 Z",
  "M234 212 C230 176 228 158 234 128 C240 158 238 176 234 212 Z",
];

export const Intro = ({ onDone }) => {
  const [show, setShow] = useState(false);
  const { t } = useLang();

  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    try {
      if (sessionStorage.getItem("ldd-intro-seen")) return;
    } catch {
      /* storage unavailable */
    }
    setShow(true);
  }, []);

  useEffect(() => {
    if (!show) return;
    const t = setTimeout(finish, 3400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  const finish = () => {
    try {
      sessionStorage.setItem("ldd-intro-seen", "1");
    } catch {
      /* storage unavailable */
    }
    setShow(false);
    onDone?.();
  };

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          data-testid="intro-animation"
          className="fixed inset-0 z-[100] bg-cream flex flex-col items-center px-6"
          exit={{ opacity: 0, transition: { duration: 0.8, ease: "easeInOut" } }}
        >
          <motion.img
            src="/logo-full.png"
            alt="Le dolci Dimore — Mantova"
            className="mt-12 md:mt-16 w-44 md:w-56"
            initial={{ opacity: 0, y: -14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.35, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          />
          <div className="flex-1 flex items-center justify-center w-full">
            <motion.svg
              viewBox="0 0 260 230"
              className="w-56 md:w-72"
              initial="hidden"
              animate="visible"
              aria-hidden="true"
            >
              {STROKES.map((d, i) => (
                <motion.path
                  key={i}
                  d={d}
                  fill="none"
                  stroke="#4A5D4E"
                  strokeWidth={i > 4 ? 1.6 : 2.4}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  custom={i}
                  variants={draw}
                />
              ))}
            </motion.svg>
          </div>
          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.9, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="text-center mb-20"
          >
            <p className="text-xs uppercase tracking-[0.28em] text-body">
              {t.intro.caption}
            </p>
          </motion.div>
          <button
            type="button"
            onClick={finish}
            data-testid="intro-skip-button"
            className="absolute bottom-8 text-xs uppercase tracking-[0.2em] text-body hover:text-terra transition-colors duration-300"
          >
            {t.intro.skip}
          </button>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
